import { deleteUser } from 'aws-amplify/auth'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { updateUserQuery } from '@/services/user/user.graphql'
import { getAmplifyClient } from '@/services/amplifyClient'
import { useSessionRuntime } from '@/context/SessionRuntimeContext'

import { userKeys } from './user.keys'
import { UserInput } from '@skop-app/lib-types'

type UserUpdateMutation = (input: Partial<UserInput>) => Promise<void>

const useUserUpdateMutation = (mutationFn: UserUpdateMutation) => {
  const queryClient = useQueryClient()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  return useMutation({
    mutationKey: ['updateUser'],
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: currentUserId
          ? userKeys.currentScoped({
              sessionScopeKey,
              currentUserId,
              impersonatedUserId,
            })
          : userKeys.current(),
      })
    },
  })
}

export const useUserUpdate = () => useUserUpdateMutation(updateUser)

export const useUserUpdateStrict = () => useUserUpdateMutation(updateUserStrict)

export const updateUserStrict = async (input: Partial<UserInput>) => {
  try {
    const result = await getAmplifyClient().graphql<{
      updateUser?: { id: string } | null
    }>({
      query: updateUserQuery,
      variables: { input },
    })
    if (result.errors?.length) {
      throw new Error(
        result.errors
          .map((error) => error.message)
          .filter(Boolean)
          .join('; ') || 'Update user failed',
      )
    }
    if (!result.data?.updateUser?.id) {
      throw new Error('Update user returned no result')
    }
  } catch (error) {
    console.error('error updating user: ', error)
    throw error
  }
}

export const updateUser = updateUserStrict

export const deleteUserAccount = async () => {
  try {
    return await deleteUser()
  } catch (error) {
    console.error('Error deleting user', error)
    return error
  }
}
